import { FC, useMemo, useState } from 'react';

import { BugoStatBrandRow, BugoStatResult } from './adminBugoStat.types';
import { formatRate, rateOf } from './adminBugoStat.util';
import { TD_CLASS, TH_CLASS } from './AnalyticsShared';

type SortKey =
  | 'feventCount'
  | 'sharedFeventCount'
  | 'unsharedFeventCount'
  | 'shareRate'
  | 'shareCountTotal'
  | 'acqMsgFeventCount'
  | 'acqMsgRate'
  | 'acqMsgTotal'
  | 'acqMsgWrittenFeventCount'
  | 'acqMsgPerWrittenFevent'
  | 'bugoCount'
  | 'sentBugoCount'
  | 'unsentBugoCount'
  | 'sentRate'
  | 'unsentFeventCount';

//* 헤더는 좁아서 줄임말을 쓴다 — 뜻이 헷갈릴 만한 칸은 title로 풀어 준다
const COLUMNS: { key: SortKey; label: string; title?: string }[] = [
  { key: 'feventCount', label: '상가' },
  { key: 'sharedFeventCount', label: '공유', title: '부고를 1회 이상 공유한 상가 수' },
  { key: 'unsharedFeventCount', label: '미공유' },
  { key: 'shareRate', label: '공유율', title: '공유 상가 ÷ 상가' },
  { key: 'shareCountTotal', label: '공유횟수', title: '공유 횟수 총합' },
  {
    key: 'acqMsgFeventCount',
    label: '조의글설정',
    title: '조의글 기능을 켠 상가 수 (실제로 글이 달렸는지와는 별개)',
  },
  { key: 'acqMsgRate', label: '설정율', title: '조의글 설정 상가 ÷ 상가' },
  { key: 'acqMsgTotal', label: '조의글수', title: '실제로 달린 조의글 수 (차단글 제외)' },
  {
    key: 'acqMsgWrittenFeventCount',
    label: '글달린상가',
    title: '조의글이 1건 이상 달린 상가 수',
  },
  {
    key: 'acqMsgPerWrittenFevent',
    label: '평균글수',
    //* 분모는 전체 상가가 아니라 '글 달린 상가' — 안 달린 곳까지 나누면 평균이 희석된다
    title: '조의글 수 ÷ 조의글 달린 상가',
  },
  { key: 'bugoCount', label: '부고', title: '상주(부고) 수' },
  { key: 'sentBugoCount', label: '발송' },
  { key: 'unsentBugoCount', label: '미발송' },
  { key: 'sentRate', label: '발송률', title: '발송 ÷ 부고' },
  {
    key: 'unsentFeventCount',
    label: '미발송상가',
    title: '부고가 있는데 한 건도 발송되지 않은 상가 수',
  },
];

/** 정렬용 수치. 비율은 분모 0이면 -1로 내려 항상 뒤로 간다 */
const sortValueOf = (row: BugoStatBrandRow, key: SortKey): number => {
  switch (key) {
    case 'shareRate':
      return rateOf(row.sharedFeventCount, row.feventCount) ?? -1;
    case 'acqMsgRate':
      return rateOf(row.acqMsgFeventCount, row.feventCount) ?? -1;
    case 'sentRate':
      return rateOf(row.sentBugoCount, row.bugoCount) ?? -1;
    case 'acqMsgPerWrittenFevent':
      return row.acqMsgWrittenFeventCount > 0
        ? row.acqMsgTotal / row.acqMsgWrittenFeventCount
        : -1;
    default:
      return row[key];
  }
};

const cellOf = (row: BugoStatBrandRow, key: SortKey): string => {
  switch (key) {
    case 'shareRate':
      return formatRate(row.sharedFeventCount, row.feventCount);
    case 'acqMsgRate':
      return formatRate(row.acqMsgFeventCount, row.feventCount);
    case 'sentRate':
      return formatRate(row.sentBugoCount, row.bugoCount);
    case 'acqMsgPerWrittenFevent':
      return row.acqMsgWrittenFeventCount > 0
        ? (row.acqMsgTotal / row.acqMsgWrittenFeventCount).toFixed(1)
        : '-';
    default:
      return row[key].toLocaleString();
  }
};

export const AdminBugoBrandTable: FC<{
  result: BugoStatResult;
  onExport: () => void;
  /** 행 클릭 시 해당 상조의 상가 목록 열기 */
  onBrandClick: (brandKey: string, brandName: string) => void;
}> = function AdminBugoBrandTable({ result, onExport, onBrandClick }) {
  const [sortKey, setSortKey] = useState<SortKey>('feventCount');
  const [desc, setDesc] = useState(true);

  const rows = useMemo(() => {
    const sorted = [...result.brands];
    sorted.sort((a, b) => {
      const diff = sortValueOf(a, sortKey) - sortValueOf(b, sortKey);
      return desc ? -diff : diff;
    });
    return sorted;
  }, [result.brands, sortKey, desc]);

  const onHeaderClick = (key: SortKey) => {
    if (key === sortKey) setDesc((prev) => !prev);
    else {
      setSortKey(key);
      setDesc(true);
    }
  };

  const totals = result.totals;

  return (
    <div className="rounded-lg bg-white p-3 shadow-smd">
      <div className="flex items-center justify-between">
        <p className="text-16 font-bold text-gray-900">
          상조별 <span className="font-normal text-gray-500">({rows.length}곳)</span>
          <span className="ml-2 text-12 font-normal text-gray-500">
            행을 누르면 상가 목록
          </span>
        </p>
        <button
          type="button"
          onClick={onExport}
          className="shrink-0 rounded-lg bg-gray-800 px-3 py-1.5 text-13 font-medium text-white hover:bg-gray-700"
        >
          엑셀
        </button>
      </div>

      <div className="mt-2 overflow-x-auto">
        <table className="w-full min-w-[1040px]">
          <thead className="border-b border-gray-200">
            <tr>
              <th
                className={`${TH_CLASS} sticky left-0 bg-white text-left`}
                scope="col"
              >
                상조
              </th>
              {COLUMNS.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`${TH_CLASS} cursor-pointer select-none hover:text-gray-900`}
                  onClick={() => onHeaderClick(column.key)}
                  title={column.title}
                >
                  {column.label}
                  {sortKey === column.key && (desc ? ' ▾' : ' ▴')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => (
              <tr
                key={row.key}
                className="cursor-pointer hover:bg-indigo-50"
                onClick={() => onBrandClick(row.key, row.brandName)}
                title={`${row.brandName} 상가 목록 보기`}
              >
                <td
                  className={`${TD_CLASS} sticky left-0 bg-white text-left font-semibold text-indigo-700 underline-offset-2 hover:underline`}
                >
                  {row.brandName}
                </td>
                {COLUMNS.map((column) => (
                  <td key={column.key} className={TD_CLASS}>
                    {cellOf(row, column.key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-gray-200">
            <tr className="bg-gray-50 font-semibold">
              <td className={`${TD_CLASS} sticky left-0 bg-gray-50 text-left`}>합계</td>
              {COLUMNS.map((column) => (
                <td key={column.key} className={TD_CLASS}>
                  {cellOf({ ...totals, key: '', brandName: '' }, column.key)}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
